import { genFileId } from '@zoryn/components/upload'
import { isCommonResultSuccess } from '@zoryn/network'
import { isArray, isObject, isString } from '@zoryn/utils'

import type { UploadUserFile } from '@zoryn/components/upload'

const IMAGE_URL_SPLIT_RE = /[,|]/
const IMAGE_RESPONSE_URL_KEYS = ['url', 'fileUrl', 'path', 'link'] as const

function normalizeImageUrl(value: unknown): string | null {
  if (value == null || value === '') return null
  if (isString(value)) {
    const trimmed = value.trim()
    if (!trimmed || trimmed.startsWith('blob:')) return null
    return trimmed
  }
  if (isObject(value)) {
    const url = (value as { url?: unknown }).url
    if (isString(url)) {
      const trimmed = url.trim()
      return trimmed || null
    }
  }
  return null
}

/** 将单元格值解析为可展示的图片 URL 列表 */
export function resolveImageCellUrls(value: unknown): string[] {
  if (value == null || value === '') return []

  if (isArray(value)) {
    return value
      .map(normalizeImageUrl)
      .filter((url): url is string => !!url)
  }

  if (isString(value)) {
    const trimmed = value.trim()
    if (!trimmed) return []

    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed) as unknown
        if (isArray(parsed)) {
          return resolveImageCellUrls(parsed)
        }
      } catch {
        // ignore invalid JSON, treat as plain URL
      }
    }

    if (IMAGE_URL_SPLIT_RE.test(trimmed)) {
      return trimmed
        .split(IMAGE_URL_SPLIT_RE)
        .map((part) => part.trim())
        .filter(Boolean)
    }

    return [trimmed]
  }

  const single = normalizeImageUrl(value)
  return single ? [single] : []
}

import { resolveImageCellSize } from '../column/grid-size-metrics'

/** @deprecated 使用 resolveImageCellSize(size) */
export const GRID_IMAGE_CELL_SIZE = resolveImageCellSize()
export const GRID_IMAGE_CELL_MAX_VISIBLE = 3

function pickUrlFromRecord(record: Record<string, unknown>): string | null {
  for (const key of IMAGE_RESPONSE_URL_KEYS) {
    const value = record[key]
    if (isString(value) && value.trim()) {
      const trimmed = value.trim()
      // 相对 path 是存储 key，不是可访问 URL，需优先使用 url 字段
      if (
        key === 'path' &&
        !/^https?:\/\//i.test(trimmed) &&
        !trimmed.startsWith('/')
      ) {
        continue
      }
      return trimmed
    }
  }
  return null
}

function getFileNameFromUrl(url: string): string {
  try {
    const pathname = new URL(url, 'http://localhost').pathname
    return pathname.split('/').pop() || ''
  } catch {
    return url.split('/').pop() || ''
  }
}

/** 从上传接口响应中解析图片 URL */
export function resolveUploadResponseUrl(response: unknown): string | null {
  if (response == null) return null
  if (isString(response)) {
    const trimmed = response.trim()
    if (!trimmed || trimmed.startsWith('blob:')) return null
    return trimmed
  }
  if (!isObject(response)) return null

  const record = response as Record<string, unknown>
  if (('code' in record || 'success' in record) && !isCommonResultSuccess(record)) {
    return null
  }

  const direct = pickUrlFromRecord(record)
  if (direct && !direct.startsWith('blob:')) return direct

  const data = record.data ?? record.result
  if (isString(data) && data.trim() && !data.trim().startsWith('blob:')) return data.trim()
  if (isObject(data)) {
    const nested = pickUrlFromRecord(data as Record<string, unknown>)
    if (nested && !nested.startsWith('blob:')) return nested
  }
  return null
}

/** 将 URL 列表写回单元格值，尽量保持原格式 */
export function serializeImageCellValue(
  urls: string[],
  originalValue?: unknown
): string | null {
  if (urls.length === 0) return null

  if (isString(originalValue) && originalValue.trim().startsWith('[')) {
    return JSON.stringify(urls)
  }
  if (isString(originalValue) && IMAGE_URL_SPLIT_RE.test(originalValue)) {
    return urls.join(',')
  }
  if (urls.length === 1) return urls[0]
  return urls.join(',')
}

export function resolveUploadFileUrl(file: UploadUserFile): string | null {
  const fromResponse = resolveUploadResponseUrl(file.response)
  if (fromResponse) return fromResponse

  if (file.url && !file.url.startsWith('blob:')) {
    return file.url
  }

  return null
}

export function extractUploadFileUrls(files: UploadUserFile[]): string[] {
  return files
    .map(resolveUploadFileUrl)
    .filter((url): url is string => !!url)
}

export function createUploadFilesFromUrls(urls: string[]): UploadUserFile[] {
  return urls.map((url, index) => ({
    name: getFileNameFromUrl(url) || `image-${index + 1}`,
    url,
    status: 'success' as const,
    uid: genFileId(),
  }))
}
